import type { Product, ProductModel } from "@dransay/api";
import { getThcCbdUnit } from "~/utils/product-unit.utils";
import type { VariantItem } from "./variant-selector.component";

type VendorPrice = NonNullable<ProductModel["vendorPrices"]>[number];

export function isVendorAvailable(vp: VendorPrice): boolean {
	return vp.available === true && (vp.stock ?? 0) > 0;
}

export function findVariantInCart(
	loaderVariants: Array<{ id: string | number }>,
	cartItems: Array<{ id: string }>,
	cartVendorId: string | undefined,
): { id: string | number } | undefined {
	if (!cartVendorId || loaderVariants.length === 0) return undefined;
	return loaderVariants.find((variant) =>
		cartItems.some((item) => item.id === variant.id.toString()),
	);
}

export function resolveVariantPriceVendorId(
	variantId: string | number,
	currentProductId: string,
	localVendorId: string | undefined,
	preferredVendorId: string | undefined,
	effectiveCartVendorId: string | undefined,
): string | undefined {
	if (variantId.toString() === currentProductId) {
		return localVendorId;
	}
	return preferredVendorId ?? effectiveCartVendorId;
}

export function hasVendorStock(
	vendorPrices: VendorPrice[] | undefined,
	vendorId: string,
): boolean {
	return (
		vendorPrices?.some(
			(vp) => vp.vendor === Number(vendorId) && isVendorAvailable(vp),
		) ?? false
	);
}

export function getVariantVendorPrice(
	vendorPrices: VendorPrice[] | undefined,
	vendorId: string | undefined,
): VendorPrice | undefined {
	const available = vendorPrices?.filter(isVendorAvailable);
	if (!available?.length) return undefined;
	if (vendorId) {
		const exact = available.find((vp) => vp.vendor === Number(vendorId));
		if (exact) return exact;
	}
	return available.find((vp) => vp.featuredVendor) ?? available[0];
}

export function deriveLabel(
	thc: number | null,
	cbd: number | null,
	unit: string | null,
	minimalAmount: number | null,
	category: Product["category"],
): string {
	if (thc != null && cbd != null) {
		return `${thc}/${cbd} ${getThcCbdUnit(category)}`;
	}
	if (minimalAmount != null && unit != null) {
		return `${minimalAmount} ${unit}`;
	}
	return "";
}

export function toVariantItem(
	variant: ProductModel,
	vendorId: string | undefined,
	cartVendorId?: string,
): VariantItem {
	const vendorPrice = getVariantVendorPrice(variant.vendorPrices, vendorId);
	return {
		id: variant.id,
		name: variant.name,
		kind: variant.kind,
		imageUrl: variant.imageUrl,
		category: variant.category,
		price: vendorPrice?.price ?? 0,
		isAvailable: !variant.isOutOfStock,
		isAvailableAtCartVendor: cartVendorId
			? hasVendorStock(variant.vendorPrices, cartVendorId)
			: null,
		label: deriveLabel(
			variant.thc,
			variant.cbd,
			variant.unit,
			variant.minimalAmount,
			variant.category,
		),
		sortValue: variant.thc ?? variant.minimalAmount ?? 0,
	};
}
